import { useCallback, useEffect, useState } from "react";
import { Tag, X } from "lucide-react";
import {
  getSelectionPlainText,
  removeKbFieldContentControlByTag,
  tagKbFieldAtSelection,
} from "../../services/gameRuleWordService";
import type { KbDraftField, KbFieldType } from "../../types/gameRuleKb";
import {
  ensureUniqueAnchor,
  toKbDocAnchor,
  toKbDocPlaceholder,
} from "../../utils/kbFieldMapper";

const KB_FIELD_TYPES: { value: KbFieldType; label: string }[] = [
  { value: "Text", label: "Text" },
  { value: "Number", label: "Number" },
  { value: "Date", label: "Date" },
  { value: "Dropdown", label: "Dropdown" },
];

export type KbFieldEditModalProps = {
  readonly open: boolean;
  readonly mode: "add" | "edit";
  readonly initialField?: KbDraftField;
  readonly existingFieldNames: readonly string[];
  readonly usedDocAnchors: readonly string[];
  readonly onCancel: () => void;
  readonly onSave: (field: KbDraftField) => void;
};

export const KbFieldEditModal = ({
  open,
  mode,
  initialField,
  existingFieldNames,
  usedDocAnchors,
  onCancel,
  onSave,
}: KbFieldEditModalProps) => {
  const [fieldId, setFieldId] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState<KbFieldType>("Text");
  const [required, setRequired] = useState(true);
  const [hint, setHint] = useState("");
  const [dropdownOptionsText, setDropdownOptionsText] = useState("");
  const [isTagged, setIsTagged] = useState(false);
  const [taggedText, setTaggedText] = useState("");
  const [docAnchor, setDocAnchor] = useState<string | undefined>();
  const [docPlaceholder, setDocPlaceholder] = useState<string | undefined>();
  const [previousDocAnchor, setPreviousDocAnchor] = useState<
    string | undefined
  >();
  const [selectionPreview, setSelectionPreview] = useState("");
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const [linkError, setLinkError] = useState(false);
  const [dropdownError, setDropdownError] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === "edit" && initialField) {
      setFieldId(initialField.id);
      setFieldName(initialField.name);
      setFieldType(initialField.fieldType);
      setRequired(initialField.required);
      setHint(initialField.hint ?? "");
      setDropdownOptionsText(
        initialField.dropdownOptions?.join(", ") ?? "",
      );
      setIsTagged(initialField.isTagged);
      setTaggedText(
        initialField.docPlaceholder ?? initialField.name,
      );
      setDocAnchor(initialField.docAnchor);
      setDocPlaceholder(initialField.docPlaceholder);
      setPreviousDocAnchor(initialField.docAnchor);
    } else {
      setFieldId(crypto.randomUUID());
      setFieldName("");
      setFieldType("Text");
      setRequired(true);
      setHint("");
      setDropdownOptionsText("");
      setIsTagged(false);
      setTaggedText("");
      setDocAnchor(undefined);
      setDocPlaceholder(undefined);
      setPreviousDocAnchor(undefined);
    }
    setTagError(null);
    setNameError(false);
    setLinkError(false);
    setDropdownError(false);
    setDuplicateError(null);
    setSelectionPreview("");
  }, [open, mode, initialField]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let disposed = false;
    const refresh = (): void => {
      void getSelectionPlainText()
        .then((t) => {
          if (!disposed) {
            setSelectionPreview(t.trim());
          }
        })
        .catch(() => {
          if (!disposed) {
            setSelectionPreview("");
          }
        });
    };
    const setup = async (): Promise<void> => {
      if (typeof Office === "undefined") {
        return;
      }
      await Office.onReady();
      if (disposed) {
        return;
      }
      refresh();
      if (Office.context?.document?.addHandlerAsync) {
        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          refresh,
        );
      }
    };
    void setup();
    const iv = window.setInterval(refresh, 1500);
    return () => {
      disposed = true;
      window.clearInterval(iv);
      if (
        typeof Office !== "undefined" &&
        Office.context?.document?.removeHandlerAsync
      ) {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
        );
      }
    };
  }, [open]);

  const handleTagContent = useCallback(async () => {
    setTagError(null);
    setLinkError(false);
    setNameError(false);
    setTagBusy(true);
    try {
      const selectedText = selectionPreview.trim();
      if (!selectedText) {
        setTagError("Select content in the Word document before tagging.");
        return;
      }

      const baseAnchor = toKbDocAnchor(selectedText);
      const used = new Set(
        usedDocAnchors.filter((a) => a && a !== previousDocAnchor),
      );
      const uniqueAnchor = ensureUniqueAnchor(baseAnchor, used);

      if (
        previousDocAnchor &&
        previousDocAnchor !== uniqueAnchor &&
        isTagged
      ) {
        await removeKbFieldContentControlByTag(previousDocAnchor);
      }

      const result = await tagKbFieldAtSelection(uniqueAnchor);
      const placeholder = toKbDocPlaceholder(result.selectedText);

      setFieldName(result.selectedText);
      setDocAnchor(result.docAnchor);
      setDocPlaceholder(placeholder);
      setTaggedText(result.selectedText);
      setIsTagged(true);
      setPreviousDocAnchor(result.docAnchor);
    } catch (e: unknown) {
      setTagError(
        e instanceof Error ? e.message : "Could not tag content in document.",
      );
    } finally {
      setTagBusy(false);
    }
  }, [
    isTagged,
    previousDocAnchor,
    selectionPreview,
    usedDocAnchors,
  ]);

  const handleSave = useCallback(() => {
    setNameError(false);
    setLinkError(false);
    setDropdownError(false);
    setDuplicateError(null);

    const trimmedName = fieldName.trim();
    let hasError = false;

    if (!isTagged || !docAnchor) {
      setLinkError(true);
      hasError = true;
    }
    if (!trimmedName) {
      setNameError(true);
      hasError = true;
    }

    const dropdownOptions =
      fieldType === "Dropdown"
        ? dropdownOptionsText
            .split(",")
            .map((o) => o.trim())
            .filter(Boolean)
        : undefined;

    if (fieldType === "Dropdown" && !dropdownOptions?.length) {
      setDropdownError(true);
      hasError = true;
    }

    const duplicate = existingFieldNames.some(
      (n) => n.trim().toLowerCase() === trimmedName.toLowerCase(),
    );
    if (duplicate) {
      setDuplicateError("A field with this name already exists.");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    onSave({
      id: fieldId,
      name: trimmedName,
      fieldType,
      required,
      hint: hint.trim() || undefined,
      docPlaceholder,
      docAnchor,
      dropdownOptions,
      isTagged: true,
    });
  }, [
    docAnchor,
    docPlaceholder,
    dropdownOptionsText,
    existingFieldNames,
    fieldId,
    fieldName,
    fieldType,
    hint,
    isTagged,
    onSave,
    required,
  ]);

  if (!open) {
    return null;
  }

  const canTag =
    Boolean(selectionPreview.trim()) &&
    !tagBusy &&
    (!isTagged || selectionPreview.trim() !== taggedText.trim());

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-field-edit-title"
    >
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-violet-300 bg-white p-4 shadow-lg">
        <div className="mb-3 flex items-center justify-between">
          <h2
            id="kb-field-edit-title"
            className="text-sm font-bold text-violet-900"
          >
            {mode === "add" ? "Add Field Manually" : "Edit Field"}
          </h2>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
            onClick={onCancel}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-violet-700">
              Link to Document Content{" "}
              <span className="text-destructive">*</span>
            </p>
            {!isTagged ? (
              <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
                Select placeholder text in the Word document, then click Tag
                Content in Document.
              </p>
            ) : (
              <div className="mt-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800">
                <p className="font-semibold">Content selected & tagged</p>
                <p className="mt-1 italic text-emerald-900">{taggedText}</p>
              </div>
            )}
            {tagError ? (
              <p className="mt-1 text-[11px] text-destructive" role="alert">
                {tagError}
              </p>
            ) : null}
            {linkError ? (
              <p className="mt-1 text-[11px] text-destructive" role="alert">
                Tag content in the document before saving this field.
              </p>
            ) : null}
            <button
              type="button"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canTag}
              onClick={() => void handleTagContent()}
            >
              <Tag className="h-3.5 w-3.5" aria-hidden />
              {tagBusy
                ? "Tagging..."
                : isTagged && !canTag
                  ? "Content Tagged in Document"
                  : "Tag Content in Document"}
            </button>
          </div>

          <label className="block text-xs font-bold text-foreground">
            Field Name <span className="text-destructive">*</span>
            <input
              className={`mt-1 w-full rounded border bg-muted/40 px-3 py-2 text-xs font-normal read-only:cursor-default read-only:text-foreground ${
                nameError ? "border-destructive" : "border-input"
              }`}
              value={fieldName}
              readOnly
              placeholder="Select and tag content in Word"
              aria-invalid={nameError}
            />
          </label>

          <label className="block text-xs font-bold text-foreground">
            Field Type <span className="text-destructive">*</span>
            <select
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
              value={fieldType}
              onChange={(event) =>
                setFieldType(event.target.value as KbFieldType)
              }
            >
              {KB_FIELD_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {fieldType === "Dropdown" ? (
            <label className="block text-xs font-bold text-foreground">
              Dropdown Options <span className="text-destructive">*</span>
              <input
                className={`mt-1 w-full rounded border bg-background px-3 py-2 text-xs font-normal ${
                  dropdownError ? "border-destructive" : "border-input"
                }`}
                value={dropdownOptionsText}
                onChange={(event) => {
                  setDropdownOptionsText(event.target.value);
                  setDropdownError(false);
                }}
                placeholder="Option 1, Option 2, Option 3"
              />
              {dropdownError ? (
                <p className="mt-1 text-[11px] text-destructive" role="alert">
                  Enter at least one dropdown option.
                </p>
              ) : null}
            </label>
          ) : null}

          <label className="flex items-center gap-2 text-xs font-bold text-foreground">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-violet-700"
              checked={required}
              onChange={(event) => setRequired(event.target.checked)}
            />
            Required field
          </label>

          <label className="block text-xs font-bold text-foreground">
            Hint / Description
            <textarea
              className="mt-1 min-h-[52px] w-full rounded border border-input bg-background px-3 py-2 text-xs font-normal"
              value={hint}
              onChange={(event) => setHint(event.target.value)}
              placeholder="Helper text shown to the user..."
              rows={2}
            />
          </label>

          {duplicateError ? (
            <p className="text-[11px] text-destructive" role="alert">
              {duplicateError}
            </p>
          ) : null}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-input bg-background px-4 py-2 text-xs font-semibold text-foreground"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded bg-violet-700 px-4 py-2 text-xs font-semibold text-white"
            onClick={handleSave}
          >
            Save Field
          </button>
        </div>
      </div>
    </div>
  );
};
